import { useEffect } from 'react'
import { router } from '@inertiajs/react'

import { useSessionStorage } from '@hooks/useSessionStorage'
import { useIsMobile } from '@components/utils/useIsMobile'
import { PageLayout } from '@components/layouts/PageLayout'
import Button from '@atoms/Button'
import { scopedTranslation } from '@frontend/utils/I18n'

import { PublishRosterDialog } from '../features/publish_roster/PublishRosterDialog'
import { isPublishable } from '../features/shifts/publishState'
import { RosterBoard } from '../features/roster_board/RosterBoard'
import { BulkActionsBar } from '../features/roster_board/BulkActionsBar'
import { BulkDeleteDialog } from '../features/roster_board/BulkDeleteDialog'
import { RosterHeader } from '../features/roster_board/RosterHeader'
import { type LayoutMode } from '../features/roster_board/layoutMode'
import { rosterCoverage } from '../features/roster_board/rosterCoverage'
import { useShiftsStore } from '../features/roster_board/stores/useShiftsStore'
import {
  ActiveFiltersBar,
  assignedSupporters,
  filterShifts,
  isFilterActive,
  RosterFilterToolbar,
  useRosterFilters,
} from '../features/filter_roster'
import { AddShiftButton } from '../features/author_shifts/AddShiftButton'
import { ExportRosterButton } from '../features/export_roster/ExportRosterButton'
import { ShiftAuthoringProvider } from '../features/author_shifts/ShiftAuthoringContext'
import { distinctRoles } from '../features/author_shifts/distinctRoles'
import type { EventWithRoster } from '../features/shifts/types'

const t = scopedTranslation('shifts_index')
const tBoard = scopedTranslation('roster_board')
const tPublish = scopedTranslation('components.publish_roster')

const LAYOUT_STORAGE_KEY = 'roster-board:layout'

type ShiftsIndexProps = {
  event: EventWithRoster
  groupId: string
}

const ShiftsIndex = ({ event, groupId }: ShiftsIndexProps) => {
  const setGroupId = useShiftsStore((state) => state.setGroupId)
  const openPublish = useShiftsStore((state) => state.openPublish)
  const reconcileSelection = useShiftsStore((state) => state.reconcileSelection)
  const selectedCount = useShiftsStore((state) => state.selectedShiftIds.size)
  useEffect(() => {
    setGroupId(groupId)
  }, [groupId, setGroupId])

  const [layout, setLayout] = useSessionStorage<LayoutMode>(LAYOUT_STORAGE_KEY, 'list')
  const isMobile = useIsMobile()
  const effectiveLayout: LayoutMode = isMobile ? 'cards' : layout

  const shifts = (event.roster?.shifts?.nodes ?? []).filter(Boolean)
  const hasPublishable = shifts.some(isPublishable)

  const selectableIds = shifts.map((shift) => shift.id).join(',')
  useEffect(() => {
    reconcileSelection(selectableIds ? selectableIds.split(',') : [])
  }, [selectableIds, reconcileSelection])
  const { filled, total } = rosterCoverage(shifts)

  const roleOptions = distinctRoles(shifts)
  const peopleOptions = assignedSupporters(shifts)
  const { filters, setRoles, setPeople, setStatus, setCoverage, removeRole, removePerson, clear } = useRosterFilters({
    roles: roleOptions,
    supporterIds: peopleOptions.map((option) => option.id),
  })
  const visibleShifts = filterShifts(shifts, filters)

  const authoring = {
    eventId: event.id,
    dates: event.spanningDates?.length ? event.spanningDates : event.startTime ? [event.startTime] : [],
    roles: distinctRoles(shifts),
    timeZone: event.ianaTimeZone ?? null,
    onChange: () => router.reload({ only: ['event'] }),
  }

  return (
    <ShiftAuthoringProvider value={authoring}>
      <PageLayout.Root>
        <PageLayout.Header icon="calendar-check-01" heading={t('page_heading')} />
        <PageLayout.Body>
          <div className="tw:flex tw:flex-col tw:gap-6 tw:pb-12 tw:w-full">
            <RosterHeader
              eventName={event.name!}
              startTime={event.startTime!}
              endTime={event.endTime!}
              shifts={shifts}
              actions={
                <>
                  <ExportRosterButton event={{ id: event.id!, name: event.name! }} />
                  <AddShiftButton />
                </>
              }
            />
            <RosterFilterToolbar
              filters={filters}
              roleOptions={roleOptions}
              peopleOptions={peopleOptions}
              onRoles={setRoles}
              onPeople={setPeople}
              onStatus={setStatus}
              onCoverage={setCoverage}
              layout={layout}
              onLayoutChange={setLayout}
              showLayoutToggle={!isMobile}
            />
            <ActiveFiltersBar
              filters={filters}
              peopleOptions={peopleOptions}
              onRemoveRole={removeRole}
              onRemovePerson={removePerson}
              onStatus={setStatus}
              onCoverage={setCoverage}
              onClear={clear}
            />
            <RosterBoard
              shifts={visibleShifts}
              totalShiftCount={shifts.length}
              filtersActive={isFilterActive(filters)}
              layout={effectiveLayout}
              onClearFilters={clear}
            />
          </div>
        </PageLayout.Body>
        <PageLayout.Footer>
          {selectedCount > 0 ? (
            <BulkActionsBar shifts={shifts} />
          ) : (
            <div className="tw:flex tw:items-center tw:justify-between tw:gap-4">
              <span className="tw:text-sm tw:text-gray-900">
                <span className="tw:font-semibold">{tBoard('spots_filled_count', { filled, total })}</span>{' '}
                {tBoard('spots_filled_suffix')}
              </span>
              <div className="tw:w-max">
                <Button size="sm" leadingIcon="check" disabled={!hasPublishable} onClick={() => openPublish('all')}>
                  {tPublish('publish_all')}
                </Button>
              </div>
            </div>
          )}
        </PageLayout.Footer>
        <PublishRosterDialog event={event} />
        <BulkDeleteDialog shifts={shifts} />
      </PageLayout.Root>
    </ShiftAuthoringProvider>
  )
}

export default ShiftsIndex
